/**
 * Legend and solve diagnostics.
 *
 * SPEC §6 requires this to be unambiguous, and §10 requires that no zone is
 * ever labelled "safe". Every label here describes what the air is modelled to
 * be doing, and the panel states the model's own limits alongside its output.
 */

import type { SolveDiagnostics } from '../physics/types';
import {
  HAZARD_COLOURS,
  HAZARD_DESCRIPTIONS,
  HAZARD_LABELS,
  rgbToCss,
  turbulenceRamp,
} from '../render/colormaps';
import type { RasterMode } from '../render/flowRenderer';
import { RASTER_LABELS, rasterRampCss, rasterScaleLabels } from './rasterLegend';

interface Props {
  raster: RasterMode;
  diagnostics: SolveDiagnostics | null;
  liftThreshold: number;
  /** free-stream wind, km/h — the denominator of the speed-up ratio */
  freeStreamKmh?: number;
}

export function Legend({ raster, diagnostics, liftThreshold, freeStreamKmh }: Props) {
  return (
    <div className="legend">
      {raster === 'hazard' ? (
        <div className="legend-block">
          <h4>Modelled air character</h4>
          <ul className="hazard-list">
            {HAZARD_LABELS.map((label, i) => (
              <li key={label}>
                <span className="swatch" style={{ background: rgbToCss(HAZARD_COLOURS[i]) }} />
                <span className="hazard-name">{label}</span>
                <span className="hazard-desc">{HAZARD_DESCRIPTIONS[label]}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : raster !== 'none' ? (
        <div className="legend-block">
          <h4>
            {RASTER_LABELS[raster]}
          </h4>
          <div
            className="ramp"
            style={{ background: rasterRampCss(raster) }}
            aria-hidden="true"
          />
          <div className="ramp-labels">
            {rasterScaleLabels(raster)?.map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="legend-block">
        <ul className="key-list">
          <li>
            <span className="key-line lift" /> Lift band — modelled climb above{' '}
            {liftThreshold.toFixed(1)} m/s
          </li>
          <li>
            <span className="key-line rotor" /> Separation / rotor zone
          </li>
        </ul>
      </div>

      {raster === 'turbulence' && (
        <div className="legend-block">
          <p className="legend-note">
            Fluctuation as a fraction of the mean wind (σᵤ/U), so it describes
            the <em>character</em> of the air rather than its strength — a rough
            reading on a light day and on a strong day look the same here. Use
            the gust layer for how hard it actually blows.
          </p>
          <ul className="ti-scale">
            <li><i style={{ background: rgbToCss(turbulenceRamp(0.16)) }} /> ~0.08 open ground, smooth</li>
            <li><i style={{ background: rgbToCss(turbulenceRamp(0.40)) }} /> ~0.20 active, terrain-textured</li>
            <li><i style={{ background: rgbToCss(turbulenceRamp(0.64)) }} /> ~0.32 strong shear, wake edge</li>
            <li><i style={{ background: rgbToCss(turbulenceRamp(1.0)) }} /> ~0.50 detached, recirculating</li>
          </ul>
          <p className="legend-note caution">
            At the violet end the gusts are around half the mean wind. At any
            soaring wind speed that is more than a wing absorbs.
          </p>
        </div>
      )}

      {raster === 'speedup' && (
        <div className="legend-block">
          <p className="legend-note">
            Local wind divided by the free stream. Red is compression — over
            crests, through gaps and around bends; blue is shelter, including the
            slow pocket behind a steep lee. Air carrying on unchanged is left
            almost transparent, so the map only marks where terrain is doing
            something to the flow.
          </p>
          <p className="legend-note">
            The scale is logarithmic, so half speed and double speed sit the same
            distance either side of centre. Multiply by the free stream for an
            absolute figure: at {(freeStreamKmh ?? 0).toFixed(0)} km/h, 1.5×
            is about {(((freeStreamKmh ?? 0) * 1.5)).toFixed(0)} km/h.
          </p>
        </div>
      )}

      {raster === 'gust' && (
        <div className="legend-block">
          <p className="legend-note">
            Absolute fluctuation in m/s — what the wing feels. Unlike turbulence
            intensity, which is a ratio and barely changes with wind strength,
            this scales with the wind, so a light day and a strong day over the
            same terrain look properly different.
          </p>
        </div>
      )}

      {raster === 'watermark' && (
        <div className="legend-block">
          <p className="legend-note">
            Everything below the chosen altitude, shaded by the same speed ratio
            the wind-variation layer uses: dark where the valley is sheltered,
            bright where terrain is squeezing the flow through a gap or along a
            gorge. The waterline marks the altitude itself.
          </p>
          <p className="legend-note caution">
            This is the gradient wind being channelled by the shape of the
            ground. Thermally driven valley and slope winds — the up-valley
            breeze, katabatic drainage — are not modelled at all, so a valley
            can read as calm here and still be moving.
          </p>
        </div>
      )}

      {raster === 'hikefly' && (
        <div className="legend-block">
          <p className="legend-note">
            The glide ratio needed to leave this point and reach open ground at
            least 300 ft below, clearing everything in between. Lower is better:
            1:1 is ground falling away as fast as you sink, 1:8 means terrain has
            stopped being the limiting factor and your wing has become it. Ground
            with no reachable opening is left blank.
          </p>
          <p className="legend-note">
            Terrain only, unless you tick the wind box — the score does not know
            what the air is doing. Check the air-character layer for that.
          </p>
          <p className="legend-note">
            With the wind box ticked, two things change. Slopes facing away from
            the wind and ground inside a rotor zone fade out; and the glide asked
            of your wing rises with the headwind you would launch into. A
            headwind cuts the ground you cover without changing how fast you
            sink, so at half your trim speed on the nose a 1:8 wing is gliding
            1:4 — the single biggest reason a slope that looks generous in still
            air is not.
          </p>
          <p className="legend-note caution">
            Elevation models are bare earth: forest, powerlines, crops, buildings
            and access rights are all invisible here. A flat patch is flat ground,
            not a landing field. Resolution is the working grid, so this is a
            shortlist of slopes worth looking at properly — never a launch site.
          </p>
        </div>
      )}

      <div className="legend-block">
        <h4>Reading the motion</h4>
        <p className="legend-note">
          Streamlines are scattered in proportion to modelled turbulence
          intensity. Long, clean, orderly lines mean smooth attached flow; short
          broken lines that wander and cross mean the model expects rough air.
        </p>
        <p className="legend-note caution">
          The mean flow on its own always looks smooth — including through a
          rotor. Judge roughness from the scatter and the colour, not from the
          fact that the lines are moving.
        </p>
      </div>

      {diagnostics && (
        <div className="legend-block diagnostics">
          <h4>Solve</h4>
          <dl>
            <div>
              <dt>Froude</dt>
              <dd>
                {Number.isFinite(diagnostics.froude) ? diagnostics.froude.toFixed(2) : '—'}{' '}
                <em>{regimeLabel(diagnostics.froudeRegime)}</em>
              </dd>
            </div>
            {Number.isFinite(diagnostics.waveLength) && (
              <div>
                <dt>Lee wavelength</dt>
                <dd>{(diagnostics.waveLength / 1000).toFixed(1)} km</dd>
              </div>
            )}
            <div>
              <dt>Relief</dt>
              <dd>{Math.round(diagnostics.reliefM)} m</dd>
            </div>
            <div>
              <dt>Peak speed-up</dt>
              <dd className={diagnostics.maxSpeedUp > 3 ? 'warn' : undefined}>
                {diagnostics.maxSpeedUp.toFixed(2)}×
              </dd>
            </div>
            <div>
              <dt>Mass residual</dt>
              <dd className={diagnostics.divergenceResidual > 0.01 ? 'warn' : undefined}>
                {diagnostics.divergenceResidual.toExponential(1)}
              </dd>
            </div>
            <div>
              <dt>Solve time</dt>
              <dd>
                {diagnostics.solveMs} ms · {diagnostics.cycles} iters
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}

function regimeLabel(regime: SolveDiagnostics['froudeRegime']): string {
  switch (regime) {
    case 'over':
      return 'flow goes over';
    case 'wave':
      return 'wave / lee rotor regime';
    case 'blocked':
      return 'blocked, air goes around';
    default:
      return 'stability unknown';
  }
}


